import React, { useState, useEffect, useRef } from 'react';
import '../../styles/Modal.css';

/**
 * Modal para solicitar recuperación de contraseña
 * Nuevo flujo:
 * 1. Usuario ingresa su nombre de usuario (documento)
 * 2. Sistema verifica si tiene correo asociado
 * 3. Si tiene correo: muestra correo parcialmente oculto y permite enviar
 * 4. Si no tiene correo: indica que debe solicitar en el colegio
 */
function RecuperarPasswordModal({ isOpen, onClose, esquema }) {
  const [paso, setPaso] = useState(1); // 1: ingresar usuario, 2: verificado
  const [usuario, setUsuario] = useState('');
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [datosUsuario, setDatosUsuario] = useState(null);
  const [enviado, setEnviado] = useState(false);

  const inputRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen, paso]);

  useEffect(() => {
    if (!isOpen) {
      // Resetear todo al cerrar
      setPaso(1);
      setUsuario('');
      setMensaje({ tipo: '', texto: '' });
      setDatosUsuario(null);
      setEnviado(false);
    }
  }, [isOpen]);

  // Paso 1: Verificar usuario
  const handleVerificarUsuario = async (e) => {
    e.preventDefault();
    setMensaje({ tipo: '', texto: '' });

    if (!usuario.trim()) {
      setMensaje({ tipo: 'error', texto: 'Ingresa tu nombre de usuario' });
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${apiUrl}/auth/verificar-usuario-recuperacion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuario: usuario.trim(), esquema })
      });
      const data = await res.json();

      if (data.success) {
        setDatosUsuario(data);
        setPaso(2);
      } else if (res.status === 404) {
        setMensaje({
          tipo: 'error',
          texto: 'Usuario no encontrado. Verifica que esté escrito correctamente.'
        });
      } else {
        setMensaje({
          tipo: 'error',
          texto: data.message || 'Error al verificar usuario'
        });
      }
    } catch (error) {
      setMensaje({
        tipo: 'error',
        texto: 'Error al verificar usuario'
      });
    } finally {
      setLoading(false);
    }
  };

  // Paso 2: Enviar correo de recuperación
  const handleEnviarCorreo = async () => {
    if (!datosUsuario?.tieneCorreo) return;

    setLoading(true);
    setMensaje({ tipo: '', texto: '' });

    try {
      const res = await fetch(`${apiUrl}/auth/recuperar-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usuarioId: datosUsuario.usuarioId,
          tipoUsuario: datosUsuario.tipoUsuario,
          esquema
        })
      });
      const data = await res.json();

      if (data.success) {
        setEnviado(true);
        setMensaje({
          tipo: 'success',
          texto: data.message
        });
      } else {
        setMensaje({
          tipo: 'error',
          texto: data.message || 'Error al enviar el correo'
        });
      }
    } catch (error) {
      setMensaje({
        tipo: 'error',
        texto: 'Error al enviar el correo'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVolver = () => {
    setPaso(1);
    setDatosUsuario(null);
    setMensaje({ tipo: '', texto: '' });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container modal-form" onClick={e => e.stopPropagation()}>
        <div className="modal-icon info">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>

        <h3 className="modal-title">Recuperar Contraseña</h3>

        {/* PASO 1: Ingresar usuario */}
        {paso === 1 && (
          <>
            <p className="modal-message">
              Ingresa tu nombre de usuario para verificar tu cuenta.
            </p>

            <form onSubmit={handleVerificarUsuario} className="modal-form-content">
              <div className="modal-form-group">
                <label htmlFor="usuario">Usuario</label>
                <input
                  ref={inputRef}
                  type="text"
                  id="usuario"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  placeholder={esquema ? "Número de documento" : "Tu usuario"}
                  autoComplete="username"
                />
              </div>

              {mensaje.texto && (
                <div className={mensaje.tipo === 'error' ? 'modal-error' : 'modal-success'}>
                  {mensaje.texto}
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-modal-confirm info"
                  disabled={loading}
                >
                  {loading ? 'Verificando...' : 'Continuar'}
                </button>
              </div>
            </form>
          </>
        )}

        {/* PASO 2: Usuario verificado */}
        {paso === 2 && datosUsuario && !enviado && (
          <>
            {datosUsuario.tieneCorreo ? (
              <>
                <div className="recuperar-usuario-info">
                  <div className="usuario-nombre">{datosUsuario.nombreUsuario}</div>
                  <div className="usuario-email-label">Se enviará un enlace de recuperación a:</div>
                  <div className="usuario-email">{datosUsuario.emailOculto}</div>
                </div>

                {mensaje.texto && (
                  <div className={mensaje.tipo === 'error' ? 'modal-error' : 'modal-success'}>
                    {mensaje.texto}
                  </div>
                )}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-modal-cancel"
                    onClick={handleVolver}
                    disabled={loading}
                  >
                    Volver
                  </button>
                  <button
                    type="button"
                    className="btn-modal-confirm info"
                    onClick={handleEnviarCorreo}
                    disabled={loading}
                  >
                    {loading ? 'Enviando...' : 'Enviar Correo'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="recuperar-sin-correo">
                  <div className="sin-correo-icon">📧</div>
                  <div className="usuario-nombre">{datosUsuario.nombreUsuario}</div>
                  <div className="sin-correo-mensaje">
                    {datosUsuario.message}
                  </div>
                </div>

                <div className="modal-actions" style={{ justifyContent: 'center' }}>
                  <button
                    type="button"
                    className="btn-modal-confirm info"
                    onClick={onClose}
                  >
                    Entendido
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* PASO 3: Correo enviado */}
        {enviado && (
          <>
            <div className="recuperar-enviado">
              <div className="enviado-icon">✉️</div>
              <div className="enviado-mensaje">{mensaje.texto}</div>
              <div className="enviado-nota">
                Revisa tu bandeja de entrada y la carpeta de spam.
              </div>
            </div>

            <div className="modal-actions" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="btn-modal-confirm info"
                onClick={onClose}
              >
                Entendido
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default RecuperarPasswordModal;
